import { useCallback, useEffect, useRef, useState } from "react";
import ResetIcon from "../../../public/images/ResetIcon";
import AnimationPanel from "../../components/AnimationPanel/AnimationPanel";
import ChatPanel from "../../components/ChatPanel/ChatPanel";
import "./chat.scss";

const CHAT_FLOW_BASE = "chat_flow_state_v1";
const UI_KEY = "chat_ui_state_v1";
const RESET_FLAG = "chat_reset_requested_v1"; // ✅ ДОБАВИЛИ

function isReloadNav() {
  try {
    const nav = performance.getEntriesByType("navigation")?.[0];
    return nav?.type === "reload";
  } catch {
    return false;
  }
}

export default function Chat() {
  // ✅ если прошлый reload был по твоей кнопке reset — чистим всё
  const resetWasRequested = sessionStorage.getItem(RESET_FLAG) === "1";
  if (resetWasRequested) {
    sessionStorage.removeItem(RESET_FLAG);
    sessionStorage.removeItem(UI_KEY);
    Object.keys(sessionStorage).forEach((k) => {
      if (k.startsWith(`${CHAT_FLOW_BASE}:`)) sessionStorage.removeItem(k);
    });
  }

  const savedUI = (() => {
    try {
      return JSON.parse(sessionStorage.getItem(UI_KEY) || "{}");
    } catch {
      return {};
    }
  })();

  const wasReload = isReloadNav() && !resetWasRequested;

  const [scene, setScene] = useState({ active: false });

  const [overlayVisible, setOverlayVisible] = useState(
    !!savedUI.overlayVisible,
  );
  const [phoneVisible, setPhoneVisible] = useState(!!savedUI.phoneVisible);
  const [authVisible, setAuthVisible] = useState(!!savedUI.authVisible);
  const [emailVisible, setEmailVisible] = useState(!!savedUI.emailVisible);

  const [platformFormKey, setPlatformFormKey] = useState(
    savedUI.platformFormKey || "",
  );
  const [companyScene, setCompanyScene] = useState(!!savedUI.companyScene);
  const [companySceneKey, setCompanySceneKey] = useState(
    savedUI.companySceneKey || "",
  );

  const [overlayAlreadyShown, setOverlayAlreadyShown] = useState(
    !!savedUI.overlayAlreadyShown,
  );
  const restoredCompanyPhase = Number.isFinite(savedUI.companyPhase)
    ? savedUI.companyPhase
    : 0;
  const [companyPhase, setCompanyPhase] = useState(restoredCompanyPhase);
  useEffect(() => {
    const onKeyDown = (e) => {
      const isMac = navigator.platform.toLowerCase().includes("mac");
      const cmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;
      const isReload =
        e.key === "F5" || (cmdOrCtrl && (e.key === "r" || e.key === "R"));
      if (isReload) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () =>
      window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, []);

  useEffect(() => {
    if (overlayVisible && !overlayAlreadyShown) setOverlayAlreadyShown(true);
  }, [overlayVisible, overlayAlreadyShown]);

  useEffect(() => {
    let persistedCompanyPhase = companyPhase;
    try {
      const raw = JSON.parse(sessionStorage.getItem(UI_KEY) || "{}");
      if (
        Number.isFinite(raw.companyPhase) &&
        raw.companyPhase > persistedCompanyPhase
      ) {
        persistedCompanyPhase = raw.companyPhase;
      }
    } catch {
      // no-op
    }

    sessionStorage.setItem(
      UI_KEY,
      JSON.stringify({
        overlayVisible,
        phoneVisible,
        authVisible,
        emailVisible,
        platformFormKey,
        companyScene,
        companySceneKey,
        overlayAlreadyShown,
        companyPhase: persistedCompanyPhase,
      }),
    );
  }, [
    overlayVisible,
    phoneVisible,
    authVisible,
    emailVisible,
    platformFormKey,
    companyScene,
    companySceneKey,
    overlayAlreadyShown,
    companyPhase,
  ]);

  const afterSceneRef = useRef(null);

  const playScene = useCallback(
    (
      afterScene,
      {
        persistOverlay = false,
        revealPhone = false,
        revealAuth = false,
        revealEmail = false,
        revealPlatformForm = false,
        platformFormKey: nextPlatformFormKey = "",
        revealCompanyScene = false,
        companySceneKey: nextCompanySceneKey = "",
      } = {},
    ) => {
      if (scene.active) return;

      if (persistOverlay) setOverlayVisible(true);

      if (revealPhone) setPhoneVisible(true);
      if (revealAuth) setAuthVisible(true);
      if (revealEmail) setEmailVisible(true);

      if (revealPlatformForm && nextPlatformFormKey)
        setPlatformFormKey(nextPlatformFormKey);

      setCompanyScene(!!revealCompanyScene);
      setCompanySceneKey(revealCompanyScene ? nextCompanySceneKey : "");

      afterSceneRef.current =
        typeof afterScene === "function" ? afterScene : null;
      setScene({ active: true });
    },
    [scene.active],
  );

  const onSceneDone = useCallback(() => {
    setScene({ active: false });
    setCompanyScene(false);
    setCompanySceneKey("");

    const fn = afterSceneRef.current;
    afterSceneRef.current = null;
    if (fn) setTimeout(fn, 150);
  }, []);

  const onResetPage = () => {
    sessionStorage.setItem(RESET_FLAG, "1");
    window.location.reload();
  };

  return (
    <div className="chat">
      <div className="chat__header">
        <h2>Chat</h2>
        <button className="chat__header__resetBtn" onClick={onResetPage}>
          <ResetIcon /> Reset page
        </button>
      </div>

      <div className="chat__body">
        <ChatPanel
          sceneActive={scene.active}
          playScene={playScene}
          storageKey={CHAT_FLOW_BASE}
        />

        <AnimationPanel
          sceneActive={scene.active}
          overlayVisible={overlayVisible}
          phoneVisible={phoneVisible}
          authVisible={authVisible}
          emailVisible={emailVisible}
          platformFormKey={platformFormKey}
          companyScene={companyScene}
          companySceneKey={companySceneKey}
          onSceneDone={onSceneDone}
          overlayAlreadyShown={overlayAlreadyShown}
          wasReload={wasReload}
          uiStorageKey={UI_KEY}
          restoredCompanyPhase={restoredCompanyPhase}
          onCompanyPhaseChange={setCompanyPhase}
        />
      </div>
    </div>
  );
}
